import * as React from 'react';
import styled from 'styled-components';
import strings from 'strings/strings.json';
import { breakpoints, sizes } from 'theme';
import LearnerCourseCard from './components/LearnerCourseCard';
import MainSection from '../../components/sections/MainSection';
import Notification, { NotificationType } from '../../components/notifiers/Notification';
import { LearnerCourseOverview } from '../../models/LearnerCourseOverview';
import ExpandableCourseList from './components/ExpandableCourseList';
import { CourseCardsContainer } from './sharedStyles/LearnerStyles';
import Header from 'components/sections/Header';
import { LearnerDashboardContext, LearnerDashboardProvider } from 'contextProviders/LearnerDashboardContext';
import { Headline2, HorizontalRule } from 'components/Elements';

export default function LearnerDashboard(): JSX.Element {
    return (
        <LearnerDashboardProvider>
            <LearnerDashboardContent />
        </LearnerDashboardProvider>
    );
}

type LearnerDashState = 'no-courses' | 'no-active-courses' | 'default';

export function LearnerDashboardContent(): JSX.Element {
    const learnerCourseOverviews = React.useContext(LearnerDashboardContext).learnerCourseOverviews!;

    const activeCourses: LearnerCourseOverview[] = [];
    const completedCourses: LearnerCourseOverview[] = [];
    const cancelledCourses: LearnerCourseOverview[] = [];

    const endOfYesterday = new Date();
    endOfYesterday.setDate(endOfYesterday.getDate() - 1);
    endOfYesterday.setHours(23, 59, 59, 59);

    for (const course of learnerCourseOverviews) {
        if (course.cancelledDate) cancelledCourses.push(course);
        else if (course.completedDate) {
            completedCourses.push(course);
        } else activeCourses.push(course);
    }

    const learnerDashState: LearnerDashState =
        learnerCourseOverviews.length === 0
            ? 'no-courses'
            : activeCourses.length === 0
              ? 'no-active-courses'
              : 'default';

    let learnerDashBody = <></>;

    switch (learnerDashState) {
        case 'no-courses':
            learnerDashBody = (
                <Notification
                    notificationType={NotificationType.INFO}
                    title={strings.learnerCoursesPage.noCoursesMessage.title}
                    description={strings.learnerCoursesPage.noCoursesMessage.description}
                    maxWidth={'100%'}
                />
            );
            break;

        case 'no-active-courses':
            learnerDashBody = (
                <>
                    <Headline2>{strings.learnerCoursesPage.completedCourses}</Headline2>
                    <CourseCardsContainer>
                        {completedCourses
                            .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                            .map((courseOverview: LearnerCourseOverview) => {
                                return (
                                    <LearnerCourseCard
                                        key={courseOverview.enrollmentId}
                                        courseOverview={courseOverview}
                                    />
                                );
                            })}
                    </CourseCardsContainer>

                    {cancelledCourses.length > 0 && (
                        <ExpandableCourseList
                            headline={<Headline2>{strings.learnerCoursesPage.cancelledCourses}</Headline2>}
                            courses={cancelledCourses}
                        />
                    )}
                </>
            );
            break;

        case 'default':
        default:
            learnerDashBody = (
                <>
                    {activeCourses.length > 0 && (
                        <>
                            <Headline2>{strings.learnerCoursesPage.activeCourses}</Headline2>
                            <CourseCardsContainer>
                                {activeCourses
                                    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                                    .map((courseOverview: LearnerCourseOverview) => {
                                        return (
                                            <LearnerCourseCard
                                                key={courseOverview.enrollmentId}
                                                courseOverview={courseOverview}
                                            />
                                        );
                                    })}
                            </CourseCardsContainer>
                        </>
                    )}

                    {completedCourses.length > 0 && (
                        <>
                            <HorizontalRule />
                            <ExpandableCourseList
                                headline={<Headline2>{strings.learnerCoursesPage.completedCourses}</Headline2>}
                                courses={completedCourses}
                            />
                        </>
                    )}

                    {cancelledCourses.length > 0 && (
                        <>
                            <HorizontalRule />
                            <ExpandableCourseList
                                headline={<Headline2>{strings.learnerCoursesPage.cancelledCourses}</Headline2>}
                                courses={cancelledCourses}
                            />
                        </>
                    )}
                </>
            );
            break;
    }

    return (
        <>
            <Header title={strings.learnerCoursesPage.title} roleSwitch />
            <MainSection>
                <ListContainer>{learnerDashBody}</ListContainer>
            </MainSection>
        </>
    );
}

const ListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingLg};
    padding: ${sizes.spacingLg} 0 ${sizes.spacingXl} 0;
    @media (max-width: ${breakpoints.sm}) {
        gap: ${sizes.spacingMd};
    }
`;
